import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Heart, Truck, Shield, MapPin, GitCompare, X, ZoomIn } from 'lucide-react';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
    isWished, toggleWish, inCompare, toggleCompare,
    subscribe, WISH_KEY_NAME, CMP_KEY_NAME,
} from '@/lib/wishlist';

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);
    const [zoomed, setZoomed] = useState(false);
    const [wished, setWished] = useState(false);
    const [compared, setCompared] = useState(false);
    const { add } = useCart();
    const { type } = useAuth();

    useEffect(() => {
        api.get(`/catalog/products/${id}`).then(({ data }) => {
            setProduct({ ...data.product, _related: data.related, _catalog: true });
        }).catch(() => {
            api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => navigate('/shop'));
        });
        setActiveImg(0);
        setWished(isWished(id));
        setCompared(inCompare(id));
        const u1 = subscribe(WISH_KEY_NAME, () => setWished(isWished(id)));
        const u2 = subscribe(CMP_KEY_NAME, () => setCompared(inCompare(id)));
        return () => { u1(); u2(); };
    }, [id, navigate]);

    const gallery = useMemo(() => {
        if (!product) return [];
        const out = [];
        if (Array.isArray(product.images)) out.push(...product.images.filter(Boolean));
        if (product.image && !out.includes(product.image)) out.unshift(product.image);
        // Dedupe
        return Array.from(new Set(out)).slice(0, 8);
    }, [product]);

    if (!product) return <div className="min-h-screen flex items-center justify-center text-inorbit-blue">Loading…</div>;

    const onAdd = async () => {
        if (type !== 'customer') { navigate(`/login?next=/product/${id}`); return; }
        try {
            await add(product.id, qty);
            toast.success('Added to cart');
        } catch { toast.error('Could not add'); }
    };
    const onWish = () => { const now = toggleWish(id); toast.success(now ? 'Saved to wishlist' : 'Removed from wishlist'); };
    const onCompare = () => { const r = toggleCompare(id); if (!r.ok) toast.error('You can compare up to 4 items'); else toast.success(r.present ? 'Added to compare' : 'Removed from compare'); };

    const discount = product.mrp > product.price ? Math.round(100 * (product.mrp - product.price) / product.mrp) : 0;

    return (
        <div data-testid="product-detail-page" className="pt-16">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-8">
                <Link to="/shop" className="text-xs uppercase tracking-widest text-text-tertiary hover:text-inorbit-orange">← Back to Shop</Link>
            </div>

            <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 pb-16">
                {/* GALLERY */}
                <div data-testid="product-gallery">
                    <div className="relative bg-cream aspect-[4/5] overflow-hidden group cursor-zoom-in" onClick={() => setZoomed(true)} data-testid="gallery-main">
                        <img src={proxyImg(gallery[activeImg])} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                        {discount >= 30 && (
                            <div className="absolute top-4 left-4 bg-inorbit-orange text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded">
                                {discount}% OFF
                            </div>
                        )}
                        <div className="absolute bottom-4 right-4 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-inorbit-blue">
                            <ZoomIn size={16} />
                        </div>
                    </div>
                    {gallery.length > 1 && (
                        <div className="flex gap-2 mt-4 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                            {gallery.map((img, i) => (
                                <button
                                    key={img + i} onClick={() => setActiveImg(i)}
                                    className={`shrink-0 w-20 h-20 overflow-hidden rounded transition-all ${activeImg === i ? 'ring-2 ring-inorbit-orange' : 'opacity-60 hover:opacity-100'}`}
                                    data-testid={`gallery-thumb-${i}`} aria-label={`Image ${i + 1}`}
                                >
                                    <img src={proxyImg(img)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* INFO */}
                <div data-testid="product-info">
                    {product._catalog
                        ? <div className="overline text-inorbit-orange">{product.brand}</div>
                        : <Link to={`/brands/${product.brand_slug}`} className="overline text-inorbit-orange">{product.brand_name}</Link>}
                    <h1 className="font-serif text-4xl md:text-5xl text-inorbit-blue mt-3 leading-tight">{product.name}</h1>
                    {product.sku && <div className="text-xs uppercase tracking-widest text-text-tertiary mt-3">SKU {product.sku}</div>}

                    <div className="mt-8 flex items-baseline gap-3">
                        <span className="font-serif text-4xl text-inorbit-blue">₹{Number(product.price).toLocaleString('en-IN')}</span>
                        {product.mrp > product.price && (
                            <>
                                <span className="text-lg text-text-tertiary line-through">₹{Number(product.mrp).toLocaleString('en-IN')}</span>
                                <span className="pill pill-yellow">{discount}% OFF</span>
                            </>
                        )}
                    </div>
                    <div className="text-xs text-text-tertiary mt-1">Inclusive of all taxes</div>

                    {(product.description || product.meta_description) && (
                        <p className="mt-8 text-text-secondary leading-relaxed">{product.description || product.meta_description}</p>
                    )}

                    <div className="mt-8 flex items-center gap-4">
                        <div className="flex items-center border-2 border-border-medium rounded">
                            <button className="px-3 py-2 hover:bg-cream" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec"><Minus size={14} /></button>
                            <span className="px-5 font-medium" data-testid="qty-value">{qty}</span>
                            <button className="px-3 py-2 hover:bg-cream" onClick={() => setQty(qty + 1)} data-testid="qty-inc"><Plus size={14} /></button>
                        </div>
                        {product.stock > 0
                            ? <span className="text-xs text-emerald-600 font-semibold">{product.stock} in stock</span>
                            : <span className="text-xs text-rose-600 font-semibold">Sold out</span>}
                    </div>

                    <div className="mt-6 flex gap-3 flex-wrap">
                        <button onClick={onAdd} disabled={product.stock <= 0} className="btn-primary disabled:opacity-50" data-testid="add-to-cart-btn"><ShoppingBag size={16} /> Add to Cart</button>
                        <button onClick={onWish} className={`btn-outline ${wished ? 'border-rose-500 text-rose-500' : ''}`} data-testid="wishlist-btn">
                            <Heart size={16} fill={wished ? 'currentColor' : 'none'} /> {wished ? 'Wishlisted' : 'Wishlist'}
                        </button>
                        <button onClick={onCompare} className={`btn-outline ${compared ? 'border-inorbit-orange text-inorbit-orange' : ''}`} data-testid="compare-btn">
                            <GitCompare size={16} /> {compared ? 'In compare' : 'Compare'}
                        </button>
                    </div>

                    <div className="mt-10 pt-8 border-t border-border-subtle space-y-4 text-sm">
                        <div className="flex items-start gap-3"><Truck size={18} strokeWidth={1.5} className="text-inorbit-orange shrink-0 mt-0.5" /><div><div className="font-medium text-inorbit-blue">Free delivery above ₹1,499</div><div className="text-text-tertiary text-xs">2-hour delivery* available at Inorbit malls</div></div></div>
                        <div className="flex items-start gap-3"><Shield size={18} strokeWidth={1.5} className="text-inorbit-orange shrink-0 mt-0.5" /><div><div className="font-medium text-inorbit-blue">Authenticity guaranteed</div><div className="text-text-tertiary text-xs">Sold by the brand store at Inorbit Mall</div></div></div>
                        <div className="flex items-start gap-3"><MapPin size={18} strokeWidth={1.5} className="text-inorbit-orange shrink-0 mt-0.5" /><div><div className="font-medium text-inorbit-blue">Earn points</div><div className="text-text-tertiary text-xs">Earn Inorbit One points on every purchase</div></div></div>
                    </div>
                </div>
            </section>

            {(product._related || product.related)?.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-28" data-testid="related-products">
                    <div className="overline mb-4">More from this category</div>
                    <h3 className="font-serif text-3xl text-inorbit-blue mb-8">You might also love</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
                        {(product._related || product.related).map((p) => (
                            <Link to={`/product/${p.id}`} key={p.id} className="card-luxury block img-zoom">
                                <div className="aspect-[4/5] overflow-hidden bg-cream"><img src={proxyImg(p.image || p.images?.[0])} alt="" className="w-full h-full object-cover" loading="lazy" /></div>
                                <div className="p-4">
                                    <div className="overline text-inorbit-orange text-[10px] mb-1 truncate">{p.brand || p.brand_name}</div>
                                    <div className="text-sm text-inorbit-blue line-clamp-2 mb-2">{p.name}</div>
                                    <div className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* ZOOM LIGHTBOX */}
            {zoomed && (
                <ZoomLightbox image={proxyImg(gallery[activeImg])} alt={product.name} onClose={() => setZoomed(false)} />
            )}
        </div>
    );
}


function ZoomLightbox({ image, alt, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-50 bg-inorbit-blue-deeper/95 backdrop-blur flex items-center justify-center p-6" onClick={onClose} data-testid="zoom-modal">
            <button data-testid="zoom-close" className="absolute top-6 right-6 w-11 h-11 bg-white/15 backdrop-blur rounded-full text-white flex items-center justify-center hover:bg-white/25" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"><X size={20} /></button>
            <img src={image} alt={alt} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
    );
}
